// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * SoundEffects - Game Sound Effects System
 * 
 * Manages game sound effects (laser fire, explosions, warp sounds, etc.)
 * Separate from voice announcements for better organization and control.
 * 
 * Features:
 * - Auto-discovery: Checks if sound files exist before playing
 * - Layering: Multiple sounds can play simultaneously
 * - Volume control: Master volume with localStorage persistence
 * - Pooling: Reuses Audio objects for better performance
 * 
 * Usage:
 *   import SoundEffects from './utils/system/SoundEffects';
 *   SoundEffects.play('laser_fire');
 *   SoundEffects.play('explosion', { volume: 0.8 });
 */

import STORAGE_KEYS from './STORAGE_KEYS';

class SoundEffectsClass {
  constructor() {
    this.audioContext = null;
    this.audioPool = new Map(); // Pool of Audio objects for reuse
    this.maxPoolSize = 10; // Max instances per sound
    this.volume = 0.5; // Default 50% volume (effects are usually louder)
    this.enabled = true;
    this.fileCache = new Map(); // Cache for file existence checks
    
    // Initialize audio context on first user interaction
    this.initialized = false;
    
    // Sound pack system
    this.soundConfig = null;
    this.currentPack = 'default';
    this.availablePacks = [];
    
    // Sound effect categories (used for organizing sounds)
    this.categories = {
      weapons: 'weapons',
      warp: 'warp',
      explosions: 'explosions',
      ui: 'ui',
      ambient: 'ambient',
      impacts: 'impacts',
    };
    
    // Sound effect registry (effect_name -> category)
    // This helps organize sounds and makes it easy to find them
    this.registry = {
      // Weapons
      'laser_fire': 'weapons',
      'laser_hit': 'weapons',
      'missile_launch': 'weapons',
      'torpedo_fire': 'weapons',
      
      // Warp
      'warp_engage': 'warp',
      'warp_disengage': 'warp',
      'warp_travel': 'warp',
      'gate_activate': 'warp',
      
      // Explosions
      'explosion_small': 'explosions',
      'explosion_medium': 'explosions',
      'explosion_large': 'explosions',
      'ship_destroyed': 'explosions',
      
      // UI
      'button_click': 'ui',
      'button_hover': 'ui',
      'modal_open': 'ui',
      'modal_close': 'ui',
      'alert_beep': 'ui',
      'scan_complete': 'ui',
      
      // Ambient
      'engine_hum': 'ambient',
      'bridge_ambient': 'ambient',
      'alarm_loop': 'ambient',
      
      // Impacts
      'hull_hit': 'impacts',
      'shield_hit': 'impacts',
      'collision': 'impacts',
    };
    
    // Load sound config
    this.loadSoundConfig();
  }

  /**
   * Load sound configuration from JSON
   */
  async loadSoundConfig() {
    try {
      const response = await fetch('/sounds/effects/sound-config.json');
      this.soundConfig = await response.json();
      this.availablePacks = this.soundConfig.soundPacks || [];
      
      // Load saved pack preference
      const savedPack = localStorage.getItem(STORAGE_KEYS.SOUND_EFFECTS_PACK);
      if (savedPack && this.availablePacks.find(p => p.id === savedPack)) {
        this.currentPack = savedPack;
      } else {
        this.currentPack = this.soundConfig.defaultPack || 'default';
      }
      
    } catch (error) {
      console.warn('[SoundEffects] Failed to load sound config, using defaults:', error);
    }
  }

  /**
   * Initialize audio context (must be called after user interaction)
   */
  initialize() {
    if (this.initialized) return;
    
    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.initialized = true;
    } catch (error) {
      console.error('[SoundEffects] Failed to initialize audio context:', error);
      this.enabled = false;
    }
  }

  /**
   * Get the full path to a sound effect file
   */
  getSoundPath(effectName) {
    const category = this.registry[effectName];
    
    if (!category) {
      console.warn(`[SoundEffects] Unknown effect: ${effectName}`);
      return null;
    }

    // Build path: /sounds/effects/{pack}/{category}/{filename}.mp3
    // Example: /sounds/effects/default/weapons/laser_fire.mp3
    const pack = this.currentPack || 'default';
    const filename = `${effectName}.mp3`;
    
    return `/sounds/effects/${pack}/${category}/${filename}`;
  }

  /**
   * Check if a sound file exists (with caching)
   */
  async checkFileExists(url) {
    // Check cache first
    if (this.fileCache.has(url)) {
      return this.fileCache.get(url);
    }

    try {
      const response = await fetch(url, { method: 'HEAD' });
      const exists = response.ok;
      
      // Cache the result
      this.fileCache.set(url, exists);
      
      return exists;
    } catch (error) {
      this.fileCache.set(url, false);
      return false;
    }
  }

  /**
   * Get an Audio object from the pool or create a new one
   */
  getAudioFromPool(url) {
    if (!this.audioPool.has(url)) {
      this.audioPool.set(url, []);
    }
    
    const pool = this.audioPool.get(url);
    
    // Find an available (not playing) audio object
    const available = pool.find(audio => audio.paused || audio.ended);
    
    if (available) {
      available.currentTime = 0; // Reset to start
      return available;
    }
    
    // Create new if pool not full
    if (pool.length < this.maxPoolSize) {
      const audio = new Audio(url);
      pool.push(audio);
      return audio;
    }
    
    // Pool full, reuse oldest
    const oldest = pool[0];
    oldest.pause();
    oldest.currentTime = 0;
    return oldest;
  }

  /**
   * Play a sound effect
   * @param {string} effectName - Effect identifier (e.g., 'laser_fire')
   * @param {object} options - Playback options
   * @param {number} options.volume - Volume override (0.0 to 1.0)
   * @param {boolean} options.loop - Whether to loop the sound
   */
  async play(effectName, options = {}) {
    if (!this.enabled) return;

    // Initialize on first play attempt
    if (!this.initialized) {
      this.initialize();
    }

    const {
      volume = this.volume,
      loop = false
    } = options;

    // Get file path
    const filePath = this.getSoundPath(effectName);
    if (!filePath) return;

    try {
      // Get audio from pool
      const audio = this.getAudioFromPool(filePath);
      audio.volume = Math.max(0, Math.min(1, volume));
      audio.loop = loop;

      await audio.play();
    } catch (error) {
      // Silently ignore missing files and autoplay policy errors
    }
  }

  /**
   * Stop a specific sound effect
   */
  stop(effectName) {
    const filePath = this.getSoundPath(effectName);
    if (!filePath) return;

    const pool = this.audioPool.get(filePath);
    if (pool) {
      pool.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    }
  }

  /**
   * Stop all sound effects
   */
  stopAll() {
    this.audioPool.forEach(pool => {
      pool.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    });
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem(STORAGE_KEYS.SOUND_EFFECTS_VOLUME, this.volume.toString());
  }

  /**
   * Get current volume
   */
  getVolume() {
    return this.volume;
  }

  /**
   * Enable/disable sound effects
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      this.stopAll();
    }
    localStorage.setItem(STORAGE_KEYS.SOUND_EFFECTS_ENABLED, enabled.toString());
  }

  /**
   * Check if sound effects are enabled
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    const savedVolume = localStorage.getItem(STORAGE_KEYS.SOUND_EFFECTS_VOLUME);
    if (savedVolume) {
      this.volume = parseFloat(savedVolume);
    }

    const savedEnabled = localStorage.getItem(STORAGE_KEYS.SOUND_EFFECTS_ENABLED);
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === 'true';
    }

  }

  /**
   * Get list of all available effects
   */
  getAvailableEffects() {
    return Object.keys(this.registry);
  }

  /**
   * Clear the file existence cache
   */
  clearCache() {
    this.fileCache.clear();
  }

  /**
   * Register a new sound effect
   * @param {string} effectName - Effect identifier
   * @param {string} category - Category name
   */
  register(effectName, category) {
    if (!this.categories[category]) {
      console.warn(`[SoundEffects] Unknown category: ${category}`);
      return;
    }
    
    this.registry[effectName] = category;
  }

  /**
   * Get available sound packs
   */
  getAvailablePacks() {
    return this.availablePacks;
  }

  /**
   * Get current sound pack
   */
  getCurrentPack() {
    return this.currentPack;
  }

  /**
   * Set sound pack
   * @param {string} packId - Pack identifier
   */
  setSoundPack(packId) {
    const pack = this.availablePacks.find(p => p.id === packId);
    
    if (!pack) {
      console.warn(`[SoundEffects] Unknown pack: ${packId}`);
      return false;
    }
    
    this.currentPack = packId;
    localStorage.setItem(STORAGE_KEYS.SOUND_EFFECTS_PACK, packId);
    
    // Clear audio pool and cache to force reload with new pack
    this.audioPool.clear();
    this.fileCache.clear();
    
    return true;
  }

  /**
   * Get pack info
   * @param {string} packId - Pack identifier
   */
  getPackInfo(packId) {
    return this.availablePacks.find(p => p.id === packId);
  }
}

// Create singleton instance
const SoundEffects = new SoundEffectsClass();

// Load settings on initialization
SoundEffects.loadSettings();

// Export singleton
export default SoundEffects;
